import {
  ActivityFeedQuery,
  ExperienceGroupSessionsQuery,
  FanExperienceCardsQuery,
  RecommendationsQuery,
} from './types';

import {
  BadgesQuery,
  CodesQuery,
  CommunicationCampaignQuery,
  EventWaitingListsQuery,
  EnhancedCodesQuery,
  EventsQuery,
  FanGroupMemberSearchAfter,
  FanGroupOwnerGroupsQuery,
  FanGroupsQuery,
  JoinedFanGroupsQuery,
  FansAnswersQuery,
  FGOWishListsValidator,
  GroupGuestsQuery,
  LanguagesQuery,
  PaginationQuery,
  PaginationQueryV2,
  PimCheckTransactionQuery,
  SearchEventRequestBody,
  SortedPaginationQueryV2,
  SurveyPaginationQuery,
  SurveyQuery,
  TimeZonesQuery,
  UsersQuery,
  VenuesQuery,
  WaitingListFiltersQuery,
  TicketsQuery,
  WaitingListQuery,
  WaitingListWithApprovalQuery,
} from './validators';
import {
  CompanyUsersQuery,
  DropOffQuery,
  PreviewGeneratedTicketRequest,
} from './types';

// Single shared "no company" sentinel. Reuses the literal already used by
// eventsKeys.detail so the company-scoping convention is consistent file-wide.
// No real companyId is ever the string 'no-company'.
export const NO_COMPANY = 'no-company';

// Collapse the three competing "no company" sentinels ('none' / '' / null /
// undefined) down to ONE factory-internal convention. Keys never emit 'none';
// request-shaping ('none' -> strip) stays in the api layer.
export const companyScope = (companyId?: string | null): string =>
  companyId && companyId !== 'none' ? companyId : NO_COMPANY;

export const usersKeys = {
  all: () => ['users'] as const,
  lists: () => [...usersKeys.all(), 'list'] as const,
  list: (params: UsersQuery) => [...usersKeys.lists(), params] as const,
  host: () => [...usersKeys.all(), 'host'] as const,
  look: (slug: string) => [...usersKeys.all(), 'look', slug] as const,
  fanGroupBySlug: (slug: string) =>
    [...usersKeys.all(), 'fanGroupBySlug', slug] as const,
  fanGroups: (userId: string) =>
    [...usersKeys.detail(userId), 'fanGroups'] as const,
  details: () => [...usersKeys.all(), 'detail'] as const,
  detail: (id: string) => [...usersKeys.details(), id] as const,
  companies: (userId: string) =>
    [...usersKeys.detail(userId), 'companies'] as const,
  mutation: () => [...usersKeys.all(), 'mutation'] as const,
};

export const ownershipsKeys = {
  all: () => ['ownerships'] as const,
  lists: () => [...ownershipsKeys.all(), 'list'] as const,
  list: (params: UsersQuery) => [...ownershipsKeys.lists(), params] as const,
  forUser: (userId: string, params: UsersQuery) =>
    [...ownershipsKeys.list(params), userId] as const,
  mutation: () => [...ownershipsKeys.all(), 'mutation'] as const,
};

export const userHostRolesKeys = {
  all: () => ['userHostRoles'] as const,
  lists: () => [...userHostRolesKeys.all(), 'list'] as const,
  list: (params: PaginationQueryV2) =>
    [...userHostRolesKeys.lists(), params] as const,
  forUser: (userId: string, params: PaginationQueryV2) =>
    [...userHostRolesKeys.list(params), userId] as const,
  mutation: () => [...userHostRolesKeys.all(), 'mutation'] as const,
};

export const profileKeys = {
  all: () => ['profile'] as const,
  admin: () => [...profileKeys.all(), 'admin'] as const,
  fan: () => [...profileKeys.all(), 'fan'] as const,
  mutation: () => [...profileKeys.all(), 'mutation'] as const,
  roles: () => [...profileKeys.all(), 'roles'] as const,
  fanGroupRoles: (fanGroupId: string) =>
    [...profileKeys.all(), 'fanGroupRoles', fanGroupId] as const,
};

export const approverKeys = {
  all: () => ['approver'] as const,
  fanGroups: (userId: string) =>
    [...approverKeys.all(), 'fanGroups', userId] as const,
};

export const fanGroupsKeys = {
  all: () => ['fanGroups'] as const,
  lists: () => [...fanGroupsKeys.all(), 'list'] as const,
  joinedLists: () => [...fanGroupsKeys.all(), 'joinedList'] as const,
  joinedList: (params: FanGroupsQuery) =>
    [...fanGroupsKeys.joinedLists(), params] as const,
  list: (params: FanGroupsQuery) => [...fanGroupsKeys.lists(), params] as const,
  approver: () => [...fanGroupsKeys.all(), 'approver'] as const,
  host: () => [...fanGroupsKeys.all(), 'host'] as const,
  fanGroupOwner: (params?: PaginationQuery | FanGroupOwnerGroupsQuery) =>
    params
      ? ([...fanGroupsKeys.all(), 'fanGroupOwner', params] as const)
      : ([...fanGroupsKeys.all(), 'fanGroupOwner'] as const),
  details: () => [...fanGroupsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...fanGroupsKeys.details(), id] as const,
  codes: (id: string) => [...fanGroupsKeys.details(), id, 'codes'] as const,
  enhancedCodes: (id: string) =>
    [...fanGroupsKeys.details(), id, 'enhancedCodes'] as const,
  accessRequests: (id: string, params: PaginationQuery) =>
    [...fanGroupsKeys.details(), id, 'accessRequests', params] as const,
  ownersBase: (id: string) =>
    [...fanGroupsKeys.details(), id, 'owners'] as const,
  owners: (id: string, params: PaginationQueryV2) =>
    [...fanGroupsKeys.ownersBase(id), params] as const,
  codesBadgesList: (id: string) =>
    [...fanGroupsKeys.details(), id, 'codesBadges'] as const,
  codesCustomInfoList: (id: string) =>
    [...fanGroupsKeys.details(), id, 'codesCustomInfo'] as const,
  badges: (id: string) => [...fanGroupsKeys.details(), id, 'badges'] as const,
  look: (id: string) => [...fanGroupsKeys.details(), id, 'look'] as const,
  filterInfo: (id: string) =>
    [...fanGroupsKeys.details(), id, 'filterInfo'] as const,
  seats: (id: string) =>
    [...fanGroupsKeys.details(), id, 'seatsNumber'] as const,
  codesLists: (id: string) => [...fanGroupsKeys.codes(id), 'list'] as const,
  codesList: (id: string, params: CodesQuery) =>
    [...fanGroupsKeys.codesLists(id), params] as const,
  enhancedCodesLists: (id: string) =>
    [...fanGroupsKeys.enhancedCodes(id), 'list'] as const,
  enhancedCodesList: (id: string, params: EnhancedCodesQuery) =>
    [...fanGroupsKeys.enhancedCodes(id), params] as const,
  mutation: () => [...fanGroupsKeys.all(), 'mutation'] as const,
  members: (id: string) => [...fanGroupsKeys.details(), id, 'members'] as const,
  membersLists: (id: string) => [...fanGroupsKeys.members(id), 'list'] as const,
  // locationFilters/badgeIds/after are additive/optional so every existing call site (which
  // only ever passed the first 3-4 args) keeps compiling unchanged.
  membersList: (
    id: string,
    params: SortedPaginationQueryV2,
    search: string,
    includeDetails?: boolean,
    locationFilters?: string[],
    badgeIds?: string[],
    after?: FanGroupMemberSearchAfter
  ) =>
    [
      ...fanGroupsKeys.membersLists(id),
      params,
      search,
      includeDetails,
      locationFilters,
      badgeIds,
      after,
    ] as const,
  // Membership lookup for a bounded set of candidate user ids (Add user modal): lives under
  // members(id) so any member mutation's invalidation refreshes it.
  membersByUserIds: (id: string, userIds: string[]) =>
    [...fanGroupsKeys.members(id), 'by-user-ids', userIds] as const,
  membersAvailableLocations: (id: string) =>
    [...fanGroupsKeys.members(id), 'available-locations'] as const,
  // Saved member-search segments (FGO Users screen phase 3) -- a sibling list to members,
  // not nested under it: segments are their own resource (/member-segments), not a view over
  // the members list.
  memberSegments: (id: string) =>
    [...fanGroupsKeys.details(), id, 'memberSegments'] as const,
  themes: () => [...fanGroupsKeys.all(), 'themes'] as const,
};

export const companyFangroupsKeys = {
  all: () => ['companyFangroups'] as const,
  lists: () => [...companyFangroupsKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...companyFangroupsKeys.lists(), params] as const,
  details: () => [...companyFangroupsKeys.all(), 'detail'] as const,
  detail: (companyId: string, fangroupId: string) =>
    [...companyFangroupsKeys.details(), companyId, fangroupId] as const,
};

export const venuesKeys = {
  all: () => ['venues'] as const,
  lists: () => [...venuesKeys.all(), 'list'] as const,
  list: (
    params: VenuesQuery,
    bodyParams?: { query?: string; companyId?: string }
  ) => [...venuesKeys.lists(), params, bodyParams] as const,
  details: () => [...venuesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...venuesKeys.details(), id] as const,
  configs: (venueId: string) =>
    [...venuesKeys.all(), venueId, 'configs'] as const,
  categories: (venueId: string) =>
    [...venuesKeys.all(), venueId, 'categories'] as const,
  configDetails: () => [...venuesKeys.all(), 'detail'] as const,
  configDetail: (id: string) => [...venuesKeys.configDetails(), id] as const,
  mutation: () => [...venuesKeys.all(), 'mutation'] as const,
};

// Normalize SearchEventRequestBody to ensure consistent query keys
// This ensures objects with the same values produce the same query key
// even if optional fields are omitted vs set to undefined
const normalizeSearchEventRequestBody = (
  body: SearchEventRequestBody
): SearchEventRequestBody => {
  const normalized: SearchEventRequestBody = {};
  if (body.query !== undefined && body.query !== null && body.query !== '') {
    normalized.query = body.query;
  }
  if (body.date !== undefined && body.date !== null && body.date !== '') {
    normalized.date = body.date;
  }
  if (body.companyId !== undefined && body.companyId !== null) {
    normalized.companyId = body.companyId;
  }
  return normalized;
};

export const eventsKeys = {
  all: () => ['events'] as const,
  lists: () => [...eventsKeys.all(), 'list'] as const,
  list: (params: EventsQuery) => [...eventsKeys.lists(), params] as const,
  adminListBase: () => [...eventsKeys.all(), 'admin'] as const,
  adminList: (params: SortedPaginationQueryV2, body: SearchEventRequestBody) =>
    [
      ...eventsKeys.adminListBase(),
      params,
      normalizeSearchEventRequestBody(body),
    ] as const,

  details: () => [...eventsKeys.all(), 'detail'] as const,
  detail: (id: string, companyId?: string) =>
    [...eventsKeys.details(), id, companyId ?? 'no-company'] as const,
  mutation: () => [...eventsKeys.all(), 'mutation'] as const,
  waitingLists: (eventId: string) =>
    [...eventsKeys.detail(eventId), 'waitingLists'] as const,

  waitingListsLists: (eventId: string) =>
    [...eventsKeys.waitingLists(eventId), 'list'] as const,
  waitingListsList: (eventId: string, params: EventWaitingListsQuery) =>
    [...eventsKeys.waitingListsLists(eventId), params] as const,
};

export const waitingListsKeys = {
  all: () => ['waitingLists'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...waitingListsKeys.all(), fanGroupId] as const,
  lists: (fanGroupId: string) =>
    [...waitingListsKeys.allPerFanGroup(fanGroupId), 'list'] as const,
  list: (fanGroupId: string, params: WaitingListQuery) =>
    [...waitingListsKeys.lists(fanGroupId), params] as const,
  filtered: (fanGroupId: string, params: WaitingListFiltersQuery) =>
    [...waitingListsKeys.lists(fanGroupId), params, 'filtered'] as const,
  paginated: (
    fanGroupId: string,
    page: number,
    pageSize: number,
    highlighted?: boolean
  ) =>
    [
      ...waitingListsKeys.lists(fanGroupId),
      { page, pageSize, highlighted },
      'paginated',
    ] as const,
  filteredPaginated: (fanGroupId: string, params: WaitingListFiltersQuery) =>
    [
      ...waitingListsKeys.lists(fanGroupId),
      params,
      'filteredPaginated',
    ] as const,
  highlighted: (fanGroupId: string, filters?: WaitingListFiltersQuery) =>
    [...waitingListsKeys.lists(fanGroupId), 'highlighted', filters] as const,
  // Sorted, so two callers asking for the same set in a different order share one cache entry.
  byIds: (waitingListIds: string[]) =>
    [...waitingListsKeys.all(), 'byIds', [...waitingListIds].sort()] as const,
  details: () => [...waitingListsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...waitingListsKeys.details(), id] as const,
  // Admin endpoint returns a different shape than the fan one: never share `detail(id)`.
  // Nested so `detail(id)` invalidations still reach it.
  adminDetail: (id: string) =>
    [...waitingListsKeys.detail(id), 'admin'] as const,
  badgesPerWL: (id: string) =>
    [...waitingListsKeys.detail(id), 'badges-state'] as const,
  additionalInfo: (id: string) =>
    [...waitingListsKeys.detail(id), id, 'additional-info'] as const,
  badges: (id: string) =>
    [...waitingListsKeys.details(), id, 'badges'] as const,
  codes: (id: string) => [...waitingListsKeys.details(), id, 'codes'] as const,
  codesLists: (id: string) => [...waitingListsKeys.codes(id), 'list'] as const,
  codesList: (id: string, params: CodesQuery) =>
    [...waitingListsKeys.codes(id), params] as const,
  scheduledActivationBadges: (id: string) =>
    [...waitingListsKeys.details(), id, 'scheduled-to-activation'] as const,
  scheduledArchivingBadges: (id: string) =>
    [...waitingListsKeys.details(), id, 'scheduled-to-archiving'] as const,
  detailPrice: (id: string, tickets: number) =>
    [...waitingListsKeys.detail(id), 'price', tickets] as const,
  transaction: (id: string) =>
    [...waitingListsKeys.detail(id), 'transaction'] as const,
  positions: (id: string) => [...waitingListsKeys.detail(id), 'positions'],
  positionsDetails: (id: string) => [
    ...waitingListsKeys.positions(id),
    'details',
  ],
  positionsList: (id: string, params: PaginationQueryV2) => [
    ...waitingListsKeys.positions(id),
    params,
  ],
  dropOffList: (id: string, params: DropOffQuery) => [
    ...waitingListsKeys.positions(id),
    'drop-off',
    params,
  ],
  seats: (id: string) => [...waitingListsKeys.detail(id), 'seats'],
  paymentInfo: (id: string) => [...waitingListsKeys.detail(id), 'payment-info'],
  seatsList: (id: string, params: PaginationQueryV2) => [
    ...waitingListsKeys.seats(id),
    params,
  ],
  seatsDetails: (id: string) => [...waitingListsKeys.seats(id), 'details'],
  // mutation: () => [...waitingListsKeys.all(), 'mutation'] as const,
};

export const joinedWaitingListsKeys = {
  all: () => ['joinedWaitingLists'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...joinedWaitingListsKeys.all(), fanGroupId] as const,
  lists: () => [...joinedWaitingListsKeys.all(), 'list'] as const,
  list: (params: JoinedFanGroupsQuery) =>
    [...joinedWaitingListsKeys.lists(), params] as const,
  paginated: (fanGroupId: string, page: number, pageSize: number) =>
    [
      ...joinedWaitingListsKeys.allPerFanGroup(fanGroupId),
      { page, pageSize },
      'paginated',
    ] as const,
  details: () => [...joinedWaitingListsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...joinedWaitingListsKeys.details(), id] as const,
};

export const waitingListsWithInvitationsKeys = {
  all: () => ['waitingListsWithInvitations'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...waitingListsWithInvitationsKeys.all(), fanGroupId] as const,
  lists: () => [...waitingListsWithInvitationsKeys.all(), 'list'] as const,
  list: (params: WaitingListQuery) =>
    [...waitingListsWithInvitationsKeys.lists(), params] as const,
  details: () => [...waitingListsWithInvitationsKeys.all(), 'detail'] as const,
  detail: (id: string) =>
    [...waitingListsWithInvitationsKeys.details(), id] as const,
};

export const waitingListTicketsKeys = {
  all: () => ['waitingListTickets'] as const,
  lists: () => [...waitingListTicketsKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...waitingListTicketsKeys.lists(), params] as const,
  paginated: (fanGroupId: string, query: TicketsQuery) =>
    [...waitingListTicketsKeys.all(), fanGroupId, query, 'paginated'] as const,
  count: (fanGroupId: string) =>
    [...waitingListTicketsKeys.all(), fanGroupId, 'count'] as const,
  details: () => [...waitingListTicketsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...waitingListTicketsKeys.details(), id] as const,
};

export const waitingListInvitationsKeys = {
  all: () => ['waitingListInvitations'] as const,
  allPerWaitingList: (id: string) =>
    [...waitingListInvitationsKeys.all(), id] as const,
  lists: (id: string) =>
    [...waitingListInvitationsKeys.allPerWaitingList(id), 'list'] as const,
  list: <QueryType>(id: string, params: QueryType) =>
    [...waitingListInvitationsKeys.lists(id), params] as const,
  mutation: () => ['mutation'] as const,
};

export const waitingListConditionsKeys = {
  all: () => ['waitingListConditions'] as const,
  allPerWaitingList: (id: string) =>
    [...waitingListConditionsKeys.all(), id] as const,
};

export const waitingListInvitationsToApproveKeys = {
  all: () => ['waitingListInvitationsToApprove'] as const,
  allPerWaitingList: (id: string) =>
    [...waitingListInvitationsToApproveKeys.all(), id] as const,
  lists: (id: string) =>
    [
      ...waitingListInvitationsToApproveKeys.allPerWaitingList(id),
      'list',
    ] as const,
  list: (id: string, params: WaitingListWithApprovalQuery) =>
    [...waitingListInvitationsToApproveKeys.lists(id), params] as const,
  details: () =>
    [...waitingListInvitationsToApproveKeys.all(), 'detail'] as const,
  detail: (id: string) =>
    [...waitingListInvitationsToApproveKeys.details(), id] as const,
  mutation: () =>
    [...waitingListInvitationsToApproveKeys.all(), 'mutation'] as const,
};

export const waitingListsApproverRequestsKeys = {
  all: () => ['waitingListsApproverRequests'] as const,
  lists: (waitingListId: string) =>
    [...waitingListsApproverRequestsKeys.all(), waitingListId, 'list'] as const,
  // details: () => [...waitingListsWithApprovalKeys.all(), 'detail'] as const,
  // detail: (id: string) =>
  //   [...waitingListsWithApprovalKeys.details(), id] as const,
};

export const waitingListsWithApprovalKeys = {
  all: () => ['waitingListsWithApproval'] as const,
  allPerFangroup: (id: string) =>
    [...waitingListsWithApprovalKeys.all(), id] as const,
  lists: (id: string) =>
    [...waitingListsWithApprovalKeys.allPerFangroup(id), 'list'] as const,
  list: (id: string, params: WaitingListWithApprovalQuery) =>
    [...waitingListsWithApprovalKeys.lists(id), params] as const,
  details: () => [...waitingListsWithApprovalKeys.all(), 'detail'] as const,
  detail: (id: string) =>
    [...waitingListsWithApprovalKeys.details(), id] as const,
};

export const surveyInstancesKeys = {
  all: () => ['surveysInstances'] as const,
  lists: () => [...surveyInstancesKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...surveyInstancesKeys.lists(), params] as const,

  details: () => [...surveyInstancesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...surveyInstancesKeys.details(), id] as const,
  listPerWL: (params: Omit<SurveyQuery, 'fangroup_id'>) =>
    [...surveyInstancesKeys.lists(), params] as const,
  listPerFan: (fanGroupId: string, extensionPoint?: string) =>
    [...surveyInstancesKeys.lists(), fanGroupId, extensionPoint] as const,
  answersBeforeJoin: (
    params: Omit<SurveyQuery, 'fangroup_id'>,
    instanceId: string
  ) => [...surveyInstancesKeys.listPerWL(params), instanceId],
  answersAfterEvent: (
    params: Omit<SurveyQuery, 'fangroup_id'>,
    instanceId: string
  ) => [...surveyInstancesKeys.listPerWL(params), instanceId],
  answersAfterSignIn: (fanGroupId: string, instanceId: string) => [
    ...surveyInstancesKeys.listPerFan(fanGroupId),
    instanceId,
  ],
  answersAtCheckout: (
    params: Omit<SurveyQuery, 'fangroup_id'>,
    instanceId: string
  ) => [...surveyInstancesKeys.listPerWL(params), instanceId],
  mutation: () => [...surveyInstancesKeys.all(), 'mutation'] as const,
};

export const surveyFansAnswers = {
  all: () => ['surveyFansAnswers'] as const,
  lists: () => [...surveyFansAnswers.all(), 'list'] as const,
  list: (params: FansAnswersQuery) =>
    [...surveyFansAnswers.lists(), params] as const,

  details: () => [...surveyFansAnswers.all(), 'detail'] as const,
  detail: (id: string) => [...surveyFansAnswers.details(), id] as const,
  listPerWL: (wlId: string, params: FansAnswersQuery) =>
    [...surveyFansAnswers.lists(), params, wlId] as const,
  listPerInstance: (
    surveyInstanceId: string,
    wlId: string,
    params: FansAnswersQuery
  ) =>
    [...surveyFansAnswers.listPerWL(wlId, params), surveyInstanceId] as const,
};

export const badgesCategoryKeys = {
  all: () => ['badgesCategory'] as const,
};

export const badgesKeys = {
  all: () => ['badges'] as const,
  lists: () => [...badgesKeys.all(), 'list'] as const,
  list: (params: BadgesQuery) => [...badgesKeys.lists(), params] as const,
  infiniteList: (params: BadgesQuery) =>
    [...badgesKeys.lists(), 'infinite', params] as const,
  details: () => [...badgesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...badgesKeys.details(), id] as const,
  mutation: () => [...badgesKeys.all(), 'mutation'] as const,
};

export const badgesEventsKeys = {
  all: () => ['badgesEvents'] as const,
  lists: () => [...badgesEventsKeys.all(), 'list'] as const,
  list: (params: PaginationQueryV2) =>
    [...badgesEventsKeys.lists(), params] as const,
  listPerUser: (fanGroupId: string, userId: string) =>
    [...badgesEventsKeys.lists(), fanGroupId, userId] as const,
};

export const fansKeys = {
  all: () => ['fans'] as const,
  lists: () => [...fansKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) => [...fansKeys.lists(), params] as const,
  listPerFG: (fanGroupId: string, params: PaginationQuery) =>
    [...fansKeys.list(params), fanGroupId] as const,
  details: () => [...fansKeys.all(), 'detail'] as const,
  detail: (id: string) => [...fansKeys.details(), id] as const,
  positionsPerFan: (
    id: string,
    fanGroupId: string,
    params: PaginationQueryV2
  ) => [...fansKeys.detail(id), id, fanGroupId, params] as const,
  activitiesPerFan: (
    id: string,
    fanGroupId: string,
    showClosed: boolean,
    params: PaginationQueryV2
  ) => [...fansKeys.detail(id), id, fanGroupId, showClosed, params] as const,
  statsPerFan: (id: string, fanGroupId: string) =>
    [...fansKeys.detail(id), fanGroupId] as const,
  accessRequestsPerFan: (id: string) =>
    [...fansKeys.detail(id), 'access-requests'] as const,
  mutation: () => [...fansKeys.all(), 'mutation'] as const,
  customInfo: (id: string) => [...fansKeys.detail(id), 'custom-info'] as const,
};

export const surveysKeys = {
  all: () => ['surveys'] as const,
  lists: () => [...surveysKeys.all(), 'list'] as const,
  list: (params: SurveyPaginationQuery) =>
    [...surveysKeys.lists(), params] as const,
  mutation: () => [...surveysKeys.all(), 'mutation'] as const,
  details: () => [...surveysKeys.all(), 'detail'] as const,
  detail: (id: string) => [...surveysKeys.details(), id] as const,
};

export const questionsKeys = {
  all: () => ['questions'] as const,
  lists: () => [...questionsKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...questionsKeys.lists(), params] as const,
  mutation: () => [...questionsKeys.all(), 'mutation'] as const,
  details: () => [...questionsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...questionsKeys.details(), id] as const,
};

export const answerSemanticKeys = {
  all: () => ['answerSemantic'] as const,
  lists: () => [...answerSemanticKeys.all(), 'list'] as const,
  list: (params: SurveyPaginationQuery) =>
    [...answerSemanticKeys.lists(), params] as const,
};

export const interestsKeys = {
  all: () => ['interests'] as const,
  lists: () => [...interestsKeys.all(), 'list'] as const,
  categories: () => [...interestsKeys.all(), 'categories'] as const,
  mutation: () => [...interestsKeys.all(), 'mutation'] as const,
};

export const fanAttributesKeys = {
  all: () => ['fan-attributes'] as const,
  lists: () => [...fanAttributesKeys.all(), 'list'] as const,
  search: () => [...fanAttributesKeys.all(), 'search'] as const,
  searchKey: (key: string) => [...fanAttributesKeys.search(), key] as const,
  mutation: () => [...fanAttributesKeys.all(), 'mutation'] as const,
};

export const adminInterestCategoriesKeys = {
  all: () => ['admin-interest-categories'] as const,
  lists: () => [...adminInterestCategoriesKeys.all(), 'list'] as const,
  list: (params: object) =>
    [...adminInterestCategoriesKeys.lists(), params] as const,
  mutation: () => [...adminInterestCategoriesKeys.all(), 'mutation'] as const,
};

export const adminInterestsKeys = {
  all: () => ['admin-interests'] as const,
  lists: () => [...adminInterestsKeys.all(), 'list'] as const,
  list: (params: object) => [...adminInterestsKeys.lists(), params] as const,
  byCategory: (categoryId: string) =>
    [...adminInterestsKeys.lists(), 'category', categoryId] as const,
  mutation: () => [...adminInterestsKeys.all(), 'mutation'] as const,
};

export const adminFanAttributesKeys = {
  all: () => ['admin-fan-attributes'] as const,
  lists: () => [...adminFanAttributesKeys.all(), 'list'] as const,
  list: (params: object) =>
    [...adminFanAttributesKeys.lists(), params] as const,
  mutation: () => [...adminFanAttributesKeys.all(), 'mutation'] as const,
};

export const questionChoicesKeys = {
  all: () => ['choices'] as const,
  lists: () => [...questionChoicesKeys.all(), 'list'] as const,
  listPerQuestion: (questionId: string) =>
    [...questionChoicesKeys.lists(), questionId] as const,
};

export const languagesKeys = {
  all: () => ['languages'] as const,
  lists: () => [...languagesKeys.all(), 'list'] as const,
  list: (params: LanguagesQuery) => [...languagesKeys.lists(), params] as const,
};

export const groupsKeys = {
  all: () => ['groups'] as const,
  lists: () => [...groupsKeys.all(), 'list'] as const,
  list: (params: LanguagesQuery) => [...groupsKeys.lists(), params] as const,
  details: () => [...groupsKeys.all(), 'detail'] as const,
  guests: (id?: string) => [...groupsKeys.details(), id, 'guests'] as const,
  guestsLists: (id?: string) => [...groupsKeys.guests(id), 'list'] as const,
  guestsList: (id?: string, params?: GroupGuestsQuery) =>
    [...groupsKeys.guestsLists(id), params] as const,

  guestsMutation: (id?: string) =>
    [...groupsKeys.guests(id), 'mutation'] as const,
};

export const barCodesKeys = {
  all: () => ['bar-codes'] as const,
  lists: () => [...barCodesKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) => [...barCodesKeys.lists(), params] as const,
};

export const timeZonesKeys = {
  all: () => ['time-zones'] as const,
  lists: () => [...timeZonesKeys.all(), 'list'] as const,
  list: (params: TimeZonesQuery) => [...timeZonesKeys.lists(), params] as const,
};

export const communicationCampaignKeys = {
  all: () => ['communicationCampaign'] as const,
  lists: () => [...communicationCampaignKeys.all(), 'list'] as const,
  list: (params: CommunicationCampaignQuery) =>
    [...communicationCampaignKeys.lists(), params] as const,
  details: () => [...communicationCampaignKeys.all(), 'detail'] as const,
  detail: (id: string) => [...communicationCampaignKeys.details(), id] as const,
  badgeRecipientsCount: (badgeId: string, fanGroupId: string) =>
    [
      ...communicationCampaignKeys.all(),
      'badgeRecipientsCount',
      badgeId,
      fanGroupId,
    ] as const,
  badgeRecipientsList: (badgeId: string, fanGroupId: string) =>
    [
      ...communicationCampaignKeys.all(),
      'badgeRecipientsList',
      badgeId,
      fanGroupId,
    ] as const,
  whatsappTemplates: () =>
    [...communicationCampaignKeys.all(), 'whatsappTemplates'] as const,
};

export const countriesKeys = {
  all: () => ['countries'] as const,
  lists: () => [...countriesKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...countriesKeys.lists(), params] as const,
};

export const currencyCodesKeys = {
  all: () => ['currency-codes'] as const,
  lists: () => [...currencyCodesKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...currencyCodesKeys.lists(), params] as const,
};

export const fanGroupOwnerKeys = {
  all: () => ['fanGroupOwner'] as const,
  lists: () => [...fanGroupOwnerKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...fanGroupOwnerKeys.lists(), params] as const,
  details: () => [...fanGroupOwnerKeys.all(), 'detail'] as const,
  detail: (id: string) => [...fanGroupOwnerKeys.details(), id] as const,
};

export const fgoInsightsKeys = {
  all: () => ['fgoInsights'] as const,
  perFanGroup: (fanGroupId: string) =>
    [...fgoInsightsKeys.all(), fanGroupId] as const,
};

export const dashboardKeys = {
  all: () => ['dashboard'] as const,
  journey: (
    fanGroupId: string,
    range: { from: string; to: string },
    contractVersion: number
  ) =>
    [
      ...dashboardKeys.all(),
      'journey',
      fanGroupId,
      range.from,
      range.to,
      contractVersion,
    ] as const,
  // The requested year range is part of the key: a range reaching outside the fetched span is a
  // different response. A range change *inside* it slices the cached years and issues no request.
  history: (
    fanGroupId: string,
    range?: { fromYear?: number; toYear?: number }
  ) =>
    [
      ...dashboardKeys.all(),
      'history',
      fanGroupId,
      range?.fromYear ?? 'no-range',
      range?.toYear ?? 'no-range',
    ] as const,
  // The period is part of the key: the same fan group's period-aware metrics differ per range.
  metrics: (fanGroupId: string, range?: { from?: string; to?: string }) =>
    [
      ...dashboardKeys.all(),
      'metrics',
      fanGroupId,
      range?.from ?? 'no-period',
      range?.to ?? 'no-period',
    ] as const,
  // No period in the key: the endpoint takes none.
  statistics: (fanGroupId: string) =>
    [...dashboardKeys.all(), 'statistics', fanGroupId] as const,
  activity: (fanGroupId: string, params: ActivityFeedQuery = {}) =>
    [...dashboardKeys.all(), 'activity', fanGroupId, params] as const,
  experienceRequestTrends: (fanGroupId: string, params: unknown) =>
    [
      ...dashboardKeys.all(),
      'experience-request-trends',
      fanGroupId,
      params,
    ] as const,
  survey: (params: unknown) =>
    [...dashboardKeys.all(), 'survey', params] as const,
};

const preferenceFanScope = (fanScope?: string): string =>
  fanScope || 'anonymous';

export const preferencesKeys = {
  all: (fanScope?: string) =>
    ['preferences', preferenceFanScope(fanScope)] as const,
  captures: (fanScope?: string) =>
    [...preferencesKeys.all(fanScope), 'capture'] as const,
  capture: (
    fanScope?: string,
    locale: string = 'auto',
    scope: string = 'all',
    fanGroupId: string = 'none'
  ) =>
    [...preferencesKeys.captures(fanScope), locale, scope, fanGroupId] as const,
  profiles: (fanScope?: string) =>
    [...preferencesKeys.all(fanScope), 'profile'] as const,
  profile: (
    fanScope?: string,
    locale: string = 'auto',
    scope: string = 'all',
    fanGroupId: string = 'none'
  ) =>
    [...preferencesKeys.profiles(fanScope), locale, scope, fanGroupId] as const,
  // FGO fan-detail view of a fan's preferences; keyed per target fan, not the viewer.
  adminProfile: (
    fanId: string,
    fanGroupId: string = 'none',
    locale: string = 'auto'
  ) => ['preferences-admin', fanId, fanGroupId, locale] as const,
};

// Admin taxonomy catalog: a single indexed query for the whole active tree —
// no list params, no per-id detail (mirrors adminInterestCategoriesKeys).
export const taxonomyKeys = {
  all: () => ['taxonomy'] as const,
  tree: () => [...taxonomyKeys.all(), 'tree'] as const,
};

export const fanGroupCustomInfoKeys = {
  all: (id: string) => ['customInfo', id] as const,
  host: (id: string) => ['customInfo', id, 'host'] as const,
  lists: (id: string) => [...fanGroupCustomInfoKeys.all(id), 'list'] as const,
  list: (id: string, params: SortedPaginationQueryV2) =>
    [...fanGroupCustomInfoKeys.lists(id), params] as const,
  details: (id: string) =>
    [...fanGroupCustomInfoKeys.all(id), 'detail'] as const,
  detail: (id: string, customInfoId: string) =>
    [...fanGroupCustomInfoKeys.details(id), customInfoId] as const,
};

/**
 * The facet request's identity: the row scope, with the Category selection dropped. The endpoint
 * deliberately ignores that selection, so leaving it in would mint a fresh cache entry for every
 * pick behind a byte-identical request.
 *
 * Exported because the api layer must strip exactly the same fields before the wire. Two lists
 * naming them separately would drift, and the key would then stop matching the request it sent.
 */
export const facetScopeOf = ({
  ticketCategories: _ticketCategories,
  includeUncategorised: _includeUncategorised,
  ...rowScope
}: FGOWishListsValidator): Omit<
  FGOWishListsValidator,
  'ticketCategories' | 'includeUncategorised'
> => rowScope;

export const fanGroupOwnerWaitingListsKeys = {
  all: () => ['fgoWaitingLists'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...fanGroupOwnerWaitingListsKeys.all(), fanGroupId] as const,
  lists: (fanGroupId: string) =>
    [
      ...fanGroupOwnerWaitingListsKeys.allPerFanGroup(fanGroupId),
      'list',
    ] as const,
  list: (fanGroupId: string, params: WaitingListQuery) =>
    [...fanGroupOwnerWaitingListsKeys.lists(fanGroupId), params] as const,

  filteredList: (
    fanGroupId: string,
    body: FGOWishListsValidator,
    params: WaitingListQuery
  ) =>
    [...fanGroupOwnerWaitingListsKeys.list(fanGroupId, params), body] as const,

  /**
   * Keyed on the *facet scope* only. `facetScopeOf` drops the Category selection, which would
   * otherwise refetch the dropdown's options on every pick. Paging, sort and the Activity period
   * cannot reach this key at all — none of them exist on `FGOWishListsValidator`.
   */
  ticketCategoryFacets: (fanGroupId: string, body: FGOWishListsValidator) =>
    [
      ...fanGroupOwnerWaitingListsKeys.allPerFanGroup(fanGroupId),
      'ticketCategoryFacets',
      facetScopeOf(body),
    ] as const,

  details: () => [...fanGroupOwnerWaitingListsKeys.all(), 'detail'] as const,
  detail: (id: string) =>
    [...fanGroupOwnerWaitingListsKeys.details(), id] as const,

  duplicate: () =>
    [...fanGroupOwnerWaitingListsKeys.all(), 'duplicate'] as const,

  approvers: (id: string) =>
    [...fanGroupOwnerWaitingListsKeys.detail(id), 'approvers'] as const,
  approverRequests: (id: string) =>
    [...fanGroupOwnerWaitingListsKeys.detail(id), 'approverRequests'] as const,
  availableSeats: (id: string) =>
    [...fanGroupOwnerWaitingListsKeys.detail(id), 'availableSeats'] as const,
  availableSecondarySeats: (id: string) =>
    [
      ...fanGroupOwnerWaitingListsKeys.detail(id),
      'availableSecondarySeats',
    ] as const,
  expiredPositions: (id: string) =>
    [...fanGroupOwnerWaitingListsKeys.detail(id), 'expiredPositions'] as const,
  // Under detail(id) so one invalidation of detail(id) also refreshes the removals list.
  remainingTicketsRemovals: (id: string) =>
    [
      ...fanGroupOwnerWaitingListsKeys.detail(id),
      'remainingTicketsRemovals',
    ] as const,
};

export const fanGroupOwnerEventsKeys = {
  all: () => ['fgoEvents'] as const,
  lists: () => [...fanGroupOwnerEventsKeys.all(), 'list'] as const,
  list: (params: EventsQuery) =>
    [...fanGroupOwnerEventsKeys.lists(), params] as const,
  filteredList: (params: EventsQuery, body: SearchEventRequestBody) =>
    [
      ...fanGroupOwnerEventsKeys.list(params),
      normalizeSearchEventRequestBody(body),
    ] as const,
  details: () => [...fanGroupOwnerEventsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...fanGroupOwnerEventsKeys.details(), id] as const,
};

export const fanGroupOwnerWaitingListNames = {
  all: () => ['fgoWaitingListNames'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...fanGroupOwnerWaitingListNames.all(), fanGroupId] as const,
  lists: () => [...fanGroupOwnerWaitingListNames.all(), 'list'] as const,
  details: () => [...fanGroupOwnerWaitingListNames.all(), 'detail'] as const,
  detail: (id: string) =>
    [...fanGroupOwnerWaitingListNames.details(), id] as const,
};

export const authConfigKeys = {
  all: () => ['auth-configs'] as const,
  pim: (provider: string) =>
    [...currencyCodesKeys.all(), 'pim', provider] as const,
  pimCheckTransaction: (provider: string, query?: PimCheckTransactionQuery) =>
    [...authConfigKeys.pim(provider), 'check-transaction', query] as const,
  oauth: (provider: string) =>
    [...currencyCodesKeys.all(), 'oauth', provider] as const,
  saml: (provider: string) =>
    [...currencyCodesKeys.all(), 'saml', provider] as const,
};

export const guestKeys = {
  all: () => ['guest'] as const,
  fanGroup: (slug: string) => [...guestKeys.all(), 'fanGroup', slug] as const,
  waitingList: (waitingListId: string) =>
    [...guestKeys.all(), 'waitingList', waitingListId] as const,
  mutation: () => [...guestKeys.all(), 'mutation'] as const,
  rejectMutation: () => [...guestKeys.mutation(), 'reject'] as const,
  acceptMutation: () => [...guestKeys.mutation(), 'accept'] as const,
  nbrOfSeatsMutation: () =>
    [...guestKeys.mutation(), 'nbrOfSeatsMutation'] as const,
  seatsExportMutation: () => [...guestKeys.mutation(), 'seatsExport'] as const,
  parkingSeatsExportMutation: () =>
    [...guestKeys.mutation(), 'parkingSeatsExport'] as const,
  surveys: () => [...guestKeys.all(), 'surveys'] as const,
  surveysList: (waitingListId?: string) =>
    [...guestKeys.surveys(), 'list', waitingListId] as const,
  attendeeInfoMutation: () =>
    [...guestKeys.mutation(), 'attendeeInfo'] as const,
  sendTicketLinkMutation: () =>
    [...guestKeys.mutation(), 'sendTicketLink'] as const,
  surveyAnswerMutation: () =>
    [...guestKeys.mutation(), 'surveyAnswer'] as const,
};

export const ticketingKeys = {
  all: () => ['ticketing'] as const,
  systems: () => [...ticketingKeys.all(), 'systems'] as const,
  systemsLists: () => [...ticketingKeys.systems(), 'list'] as const,

  systemList: (params: PaginationQuery) =>
    [...ticketingKeys.systemsLists(), params] as const,

  details: () => [...ticketingKeys.systems(), 'detail'] as const,
  detail: (systemId: string) => [...ticketingKeys.details(), systemId] as const,

  //
  ticketPools: (systemId: string) =>
    [...ticketingKeys.detail(systemId), 'ticketPools'] as const,

  ticketPoolsLists: (systemId: string) =>
    [...ticketingKeys.ticketPools(systemId), 'list'] as const,

  ticketPoolList: (systemId: string, params?: PaginationQuery) =>
    [...ticketingKeys.ticketPoolsLists(systemId), params] as const,
  ticketPoolDetail: (ticketPoolId: string) =>
    [...ticketingKeys.all(), 'ticketPools', ticketPoolId] as const,
  ticketPoolPreview: (ticketPoolId: string) =>
    [...ticketingKeys.all(), 'ticketPools', ticketPoolId, 'preview'] as const,
  fgoTicketPoolDetail: (ticketPoolId: string) =>
    [...ticketingKeys.all(), 'ticketPools', ticketPoolId, 'fgo'] as const,

  // Minting/render progress of a single generated pool (polled). Filed under the shared
  // 'ticketPools' subtree (like ticketPoolPreview) so a pool-scoped invalidation reaches it.
  composedPoolProgress: (ticketPoolId: string) =>
    [...ticketingKeys.all(), 'ticketPools', ticketPoolId, 'progress'] as const,

  generatedTicketPreview: (
    groupId: string,
    body: PreviewGeneratedTicketRequest
  ) =>
    [...ticketingKeys.all(), 'generatedTicketPreview', groupId, body] as const,
};

export const paymentSystemsKeys = {
  all: () => ['payment-systems'] as const,
  lists: () => [...paymentSystemsKeys.all(), 'list'] as const,
  list: (params: PaginationQuery) =>
    [...paymentSystemsKeys.lists(), params] as const,
};

export const themesKeys = {
  all: () => ['themes'] as const,
  lists: () => [...themesKeys.all(), 'list'] as const,
  list: (params: PaginationQueryV2 & { query?: string }) =>
    [...themesKeys.lists(), params] as const,
  details: () => [...themesKeys.all(), 'detail'] as const,
  detail: (id: string, companyId?: string) =>
    [...themesKeys.details(), id, companyScope(companyId)] as const,
  defaultTheme: (companyId?: string) =>
    [...themesKeys.details(), 'default', companyScope(companyId)] as const,
  // Folded in from the former standalone fanGroupThemeKeys: lives under the
  // 'themes' root so a single themesKeys.all() invalidation covers it.
  fangroupTheme: (fangroupId: string, companyId?: string) =>
    [
      ...themesKeys.all(),
      'fangroup',
      fangroupId,
      companyScope(companyId),
    ] as const,
  mutation: () => [...themesKeys.all(), 'mutation'] as const,
};

export const templatesKeys = {
  all: () => ['templates'] as const,
  lists: () => [...templatesKeys.all(), 'list'] as const,
  list: (params: PaginationQueryV2 & { query?: string }) =>
    [...templatesKeys.lists(), params] as const,
  theme: (
    themeId: string,
    companyId?: string,
    params?: PaginationQueryV2 & { search?: string }
  ) =>
    params
      ? ([
          ...templatesKeys.all(),
          'theme',
          themeId,
          companyScope(companyId),
          params,
        ] as const)
      : ([
          ...templatesKeys.all(),
          'theme',
          themeId,
          companyScope(companyId),
        ] as const),
  default: (companyId?: string) =>
    [...templatesKeys.all(), 'default', companyScope(companyId)] as const,
  details: () => [...templatesKeys.all(), 'detail'] as const,
  detail: (id: string, companyId?: string) =>
    [...templatesKeys.details(), id, companyScope(companyId)] as const,
  mutation: () => [...templatesKeys.all(), 'mutation'] as const,
};

export const personalizedRsvpPreviewAssetsKeys = {
  all: () => ['personalized-rsvp-preview-assets'] as const,
  detail: (
    fanGroupId: string,
    waitingListId: string | undefined,
    assignationMode: string,
    locale?: string
  ) =>
    [
      ...personalizedRsvpPreviewAssetsKeys.all(),
      fanGroupId || 'no-fan-group',
      waitingListId || 'all-waiting-lists',
      assignationMode || 'no-assignation-mode',
      locale || 'default-locale',
    ] as const,
};

export const companiesKeys = {
  all: () => ['companies'] as const,
  lists: () => [...companiesKeys.all(), 'list'] as const,
  list: (params?: any) => [...companiesKeys.lists(), params] as const,
  details: () => [...companiesKeys.all(), 'detail'] as const,
  detail: (companyId: string) =>
    [...companiesKeys.details(), companyId] as const,
  allAdmins: (params?: any) =>
    [...companiesKeys.all(), 'admins', params] as const,
  admins: (companyId: string, params?: any) =>
    params
      ? ([...companiesKeys.detail(companyId), 'admins', params] as const)
      : ([...companiesKeys.detail(companyId), 'admins'] as const),
  fangroups: (companyId: string, params?: any) =>
    params
      ? ([...companiesKeys.detail(companyId), 'fangroups', params] as const)
      : ([...companiesKeys.detail(companyId), 'fangroups'] as const),
  badges: (companyId: string) =>
    [...companiesKeys.detail(companyId), 'badges'] as const,
  usersBase: (companyId: string) =>
    [...companiesKeys.detail(companyId), 'users'] as const,
  users: (
    companyId: string,
    // The infinite variant folds its own flag into the key.
    params?: CompanyUsersQuery & { infinite?: boolean }
  ) => [...companiesKeys.usersBase(companyId), params] as const,
  user: (companyId: string, userId: string) =>
    [...companiesKeys.usersBase(companyId), userId] as const,
  myCompanies: () => [...companiesKeys.all(), 'me'] as const,
  mutation: () => [...companiesKeys.all(), 'mutation'] as const,
};

export const internalNotesKeys = {
  all: () => ['internalNotes'] as const,
  byWaitingList: (waitingListId: string) =>
    [...internalNotesKeys.all(), 'byWaitingList', waitingListId] as const,
};

export const ticketLimitsKeys = {
  all: () => ['ticketLimits'] as const,
  list: (fanGroupId: string) =>
    [...ticketLimitsKeys.all(), 'list', fanGroupId] as const,
  usageAll: () => [...ticketLimitsKeys.all(), 'usage'] as const,
  usage: (fanGroupId: string) =>
    [...ticketLimitsKeys.usageAll(), fanGroupId] as const,
};

export const experienceGroupsKeys = {
  all: () => ['experienceGroups'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...experienceGroupsKeys.all(), fanGroupId] as const,
  list: (fanGroupId: string) =>
    [...experienceGroupsKeys.allPerFanGroup(fanGroupId), 'list'] as const,
  sessions: (fanGroupId: string, query: ExperienceGroupSessionsQuery) =>
    [
      ...experienceGroupsKeys.allPerFanGroup(fanGroupId),
      'sessions',
      query,
    ] as const,
  // 'fan' never equals a fan-group id, so these do not overlap allPerFanGroup.
  fan: () => [...experienceGroupsKeys.all(), 'fan'] as const,
  fanCards: (fanGroupId: string, query: FanExperienceCardsQuery) =>
    [...experienceGroupsKeys.fan(), fanGroupId, 'cards', query] as const,
  fanDays: (fanGroupId: string, experienceGroupId: string) =>
    [...experienceGroupsKeys.fan(), fanGroupId, 'days', experienceGroupId] as const,
  fanSession: (fanGroupId: string, eventId: string) =>
    [...experienceGroupsKeys.fan(), fanGroupId, 'session', eventId] as const,
  recommendations: (fanGroupId: string, query: RecommendationsQuery) =>
    [...experienceGroupsKeys.fan(), fanGroupId, 'recommendations', query] as const,
  fanRequests: (fanGroupId: string, ticketReady: boolean) =>
    [...experienceGroupsKeys.fan(), fanGroupId, 'requests', ticketReady] as const,
};

export const fanSurveysKeys = {
  all: () => ['fanSurveys'] as const,
  byFan: (fanId: string, fanGroupId: string) =>
    [...fanSurveysKeys.all(), fanId, fanGroupId] as const,
};

export const whatsappConfigKeys = {
  all: () => ['whatsappConfig'] as const,
  config: (fanGroupId: string) =>
    [...whatsappConfigKeys.all(), fanGroupId] as const,
  senderNumbers: (fanGroupId: string) =>
    [...whatsappConfigKeys.all(), 'senderNumbers', fanGroupId] as const,
  templateOptions: (fanGroupId: string) =>
    [...whatsappConfigKeys.all(), fanGroupId, 'templateOptions'] as const,
};

// Bulk-invite (audience invitations). Everything lives under the single
// 'audienceInvitations' root — including the availability config — so one
// audienceInvitationsKeys.all() invalidation covers the whole feature, while
// mutations that only touch rows can stay narrow and invalidate lists(id).
export const audienceInvitationsKeys = {
  all: () => ['audienceInvitations'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...audienceInvitationsKeys.all(), fanGroupId] as const,
  lists: (fanGroupId: string) =>
    [...audienceInvitationsKeys.allPerFanGroup(fanGroupId), 'list'] as const,
  list: <QueryType>(fanGroupId: string, params: QueryType) =>
    [...audienceInvitationsKeys.lists(fanGroupId), params] as const,
  // Keyed by the sorted email list so re-mounting the Review step for the same
  // upload is a cache hit regardless of row order.
  preview: (fanGroupId: string, emails: string[]) =>
    [
      ...audienceInvitationsKeys.allPerFanGroup(fanGroupId),
      'preview',
      [...emails].sort().join('|'),
    ] as const,
  config: (fanGroupId: string) =>
    [...audienceInvitationsKeys.allPerFanGroup(fanGroupId), 'config'] as const,
};

// ticket scanner; the device-side status key is scoped by the session token,
// the only identifier the device holds
export const scannerKeys = {
  all: () => ['scanner'] as const,
  activation: (waitingListId: string) =>
    [...scannerKeys.all(), 'activation', waitingListId] as const,
  sessions: (waitingListId: string) =>
    [...scannerKeys.all(), 'sessions', waitingListId] as const,
  presence: (waitingListId: string) =>
    [...scannerKeys.all(), 'presence', waitingListId] as const,
  feed: (waitingListId: string, limit?: number) =>
    [...scannerKeys.all(), 'feed', waitingListId, limit] as const,
  attendees: (waitingListId: string) =>
    [...scannerKeys.all(), 'attendees', waitingListId] as const,
  deviceSessionStatus: (token: string) =>
    [...scannerKeys.all(), 'deviceSessionStatus', token] as const,
};

// Smart Invites. One 'smartInvites' root so a single smartInvitesKeys.all() invalidation covers
// the feature, and one allPerFanGroup prefix under it so flipping the capability can invalidate a
// whole fan group without touching a sibling's cache.
export const smartInvitesKeys = {
  all: () => ['smartInvites'] as const,
  allPerFanGroup: (fanGroupId: string) =>
    [...smartInvitesKeys.all(), fanGroupId] as const,
  config: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'config'] as const,
  // Slug-keyed, not fan-group-keyed: the fan area asks before it has an id.
  audienceCapability: (slug: string) =>
    [...smartInvitesKeys.all(), 'audienceCapability', slug] as const,

  catalogue: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'catalogue'] as const,
  catalogueList: <Q>(fanGroupId: string, params: Q) =>
    [...smartInvitesKeys.catalogue(fanGroupId), params] as const,

  invites: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'invites'] as const,
  inviteList: <Q>(fanGroupId: string, params: Q) =>
    [...smartInvitesKeys.invites(fanGroupId), 'list', params] as const,
  invite: (fanGroupId: string, inviteId: string) =>
    [...smartInvitesKeys.invites(fanGroupId), inviteId] as const,
  // Under the invite, so a send that invalidates the invite refreshes its mails too.
  inviteMails: (fanGroupId: string, inviteId: string) =>
    [...smartInvitesKeys.invite(fanGroupId, inviteId), 'mails'] as const,
  inviteConversation: (fanGroupId: string, inviteId: string) =>
    [...smartInvitesKeys.invite(fanGroupId, inviteId), 'conversation'] as const,
  // The calling Host's own threads of both kinds, for the Messages tab.
  hostConversations: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'host-conversations',
    ] as const,
  // The calling Host's own unread counts, one entry per invite with any.
  unreadConversations: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'unread-conversations',
    ] as const,
  inviteFgoConversation: (fanGroupId: string, inviteId: string) =>
    [
      ...smartInvitesKeys.invite(fanGroupId, inviteId),
      'fgo-conversation',
    ] as const,
  // The caller's unread Host↔FGO messages per invite: a Host's own, every invite for an FGO.
  unreadFgoConversations: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'unread-fgo-conversations',
    ] as const,
  // Only the invites an Approvals row opens: the ones with an approval or a remediation.
  unreadFgoApprovalConversations: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.unreadFgoConversations(fanGroupId),
      'approvals',
    ] as const,
  claimInvites: (fanGroupId: string, selectionIds: string[]) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'claim-invites',
      selectionIds,
    ] as const,
  // The FGO's read-only list of Host↔Guest threads on one Experience.
  allExperienceConversations: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'experience-conversations',
    ] as const,
  experienceConversations: (fanGroupId: string, waitingListId: string) =>
    [
      ...smartInvitesKeys.allExperienceConversations(fanGroupId),
      waitingListId,
    ] as const,

  limitations: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'limitations'] as const,
  blackouts: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'blackouts'] as const,

  // The FGO's Logging reads. The export appends a row, so it invalidates `reporting` whole.
  reporting: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'reporting'] as const,
  auditEvents: <Q>(fanGroupId: string, params: Q) =>
    [
      ...smartInvitesKeys.reporting(fanGroupId),
      'audit-events',
      params,
    ] as const,
  guestEvaluations: <Q>(fanGroupId: string, guestId: string, params: Q) =>
    [
      ...smartInvitesKeys.reporting(fanGroupId),
      'evaluations',
      guestId,
      params,
    ] as const,

  // The Host's read-only Compliance tab, scoped server-side to the caller's own Guests. Under
  // allPerFanGroup, so an approval decision refreshes its counts.
  hostCompliance: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'host-compliance',
    ] as const,
  hostComplianceSummary: (fanGroupId: string, year?: number) =>
    [...smartInvitesKeys.hostCompliance(fanGroupId), 'summary', year] as const,
  hostAuditEvents: <Q>(fanGroupId: string, params: Q) =>
    [
      ...smartInvitesKeys.hostCompliance(fanGroupId),
      'audit-events',
      params,
    ] as const,
  hostBlackouts: (fanGroupId: string) =>
    [...smartInvitesKeys.hostCompliance(fanGroupId), 'blackouts'] as const,

  // Kept as a distinct root rather than under allPerFanGroup so a capability-toggle invalidation
  // does not also blow away an in-flight employer search — nothing needs the two invalidated
  // together. The rows themselves are global; the endpoint nests under a fan group only so the
  // handler has one to run the availability gate against.
  employersRoot: () => [...smartInvitesKeys.all(), 'employers'] as const,
  employers: <Q>(fanGroupId: string, params: Q) =>
    [...smartInvitesKeys.employersRoot(), fanGroupId, params] as const,
  // Under employersRoot so a merge refreshes the roster list and every directory search at once.
  rosterEmployers: <Q>(fanGroupId: string, params: Q) =>
    [
      ...smartInvitesKeys.employersRoot(),
      fanGroupId,
      'roster',
      params,
    ] as const,

  // Everything guest-facing hangs off `me`: a Guest's identity, not a fan group id, is what
  // scopes it — one menu read can span several fan groups and several Hosts.
  me: () => [...smartInvitesKeys.all(), 'me'] as const,
  myMenu: () => [...smartInvitesKeys.me(), 'menu'] as const,
  // Per fan group: a Declaration belongs to the guest row, and one identity has one per fan group.
  myDeclaration: (fanGroupId: string) =>
    [...smartInvitesKeys.me(), 'declaration', fanGroupId] as const,
  myConversation: (inviteId: string) =>
    [...smartInvitesKeys.me(), 'conversation', inviteId] as const,
  myDeclarationEmployers: (search: string) =>
    [...smartInvitesKeys.me(), 'declaration-employers', search] as const,
  // Per offer, not per waiting list: a Guest reaches the number through an offer they hold, and
  // the offer id is the only identifier the availability route accepts.
  myOfferAvailability: (offerId: string) =>
    [...smartInvitesKeys.me(), 'offer-availability', offerId] as const,
  myTickets: (waitingListId: string) =>
    [...smartInvitesKeys.me(), 'tickets', waitingListId] as const,

  // The calling Host's own party on one Experience. Keyed by Experience alone: the route has no
  // other addressee, and a second Host signed into the same browser is a different session.
  hostParticipation: (waitingListId: string) =>
    [...smartInvitesKeys.all(), 'host-participation', waitingListId] as const,

  // The FGO side is scoped by Experience, not by fan group: one screen is one waiting list, and
  // assigning a spare has to invalidate exactly that Experience's availability and queue.
  experience: (waitingListId: string) =>
    [...smartInvitesKeys.all(), 'experiences', waitingListId] as const,
  experienceAvailability: (waitingListId: string) =>
    [...smartInvitesKeys.experience(waitingListId), 'availability'] as const,
  reserveQueue: (waitingListId: string) =>
    [...smartInvitesKeys.experience(waitingListId), 'reserve'] as const,
  ticketFiles: (waitingListId: string) =>
    [...smartInvitesKeys.experience(waitingListId), 'ticketFiles'] as const,
  // The Experiences where the calling Host has people to ticket (an FGO: every Host's).
  fulfilmentExperiences: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'fulfilment-experiences',
    ] as const,
  // Who needs tickets and the files to bind: the Host's own people, or every Host's for an FGO.
  fulfilment: (waitingListId: string) =>
    [...smartInvitesKeys.experience(waitingListId), 'fulfilment'] as const,
  // A Host's own Guests' return requests, or every Host's for an FGO.
  returns: (waitingListId: string) =>
    [...smartInvitesKeys.experience(waitingListId), 'returns'] as const,

  // The FGO's Insight: read-only, keyed by the UTC year it counts.
  insight: (fanGroupId: string, year: number) =>
    [...smartInvitesKeys.reporting(fanGroupId), 'insight', year] as const,
  guestInsight: (fanGroupId: string, guestId: string, year: number) =>
    [
      ...smartInvitesKeys.reporting(fanGroupId),
      'insight',
      year,
      'guests',
      guestId,
    ] as const,
  // Under allPerFanGroup, so a decision's fan-group invalidation also refreshes the Host roster.
  approvals: (fanGroupId: string) =>
    [...smartInvitesKeys.allPerFanGroup(fanGroupId), 'approvals'] as const,
  approvalList: <Q>(fanGroupId: string, params: Q) =>
    [...smartInvitesKeys.approvals(fanGroupId), 'list', params] as const,
  blackoutRemediations: (fanGroupId: string) =>
    [
      ...smartInvitesKeys.allPerFanGroup(fanGroupId),
      'blackout-remediations',
    ] as const,
  blackoutRemediationList: <Q>(fanGroupId: string, params: Q) =>
    [
      ...smartInvitesKeys.blackoutRemediations(fanGroupId),
      'list',
      params,
    ] as const,
};

export const seatingPlanKeys = {
  all: () => ['seatingPlan'] as const,
  plan: (waitingListId: string) =>
    [...seatingPlanKeys.all(), waitingListId] as const,
  fanSeatOptions: (waitingListId: string, fanId: string) =>
    [...seatingPlanKeys.plan(waitingListId), 'fanSeatOptions', fanId] as const,
  poolReads: (waitingListId: string) =>
    [...seatingPlanKeys.all(), 'poolReads', waitingListId] as const,
};
